import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import {
    CouponsService,
    CreateCouponRequestModel,
    CouponDetailInterface,
    CouponDiscountTypeEnum,
    CreateCouponPlanScopeInterface,
    TradingPlanService
} from '@spa/api';
import { ServerResponse } from '@spa/api';

import {
    CouponDiscountValueValidators,
    CouponFormMessages,
    CouponPortalIdValue,
    CouponPlanScopeValidatorKey,
    CreateCouponControls,
    CreateCouponFormControlsInterface
} from 'appAssets/descriptors/panel-coupons.descriptor';
import { BaseControl, Helper } from 'appShared/classes';

import { CouponControlErrorMessages } from './coupon-form-errors';
import { CouponCapValue, CouponRedemptionCapsValid } from './coupon-redemption-caps';
import { CouponPlanScopesFromControlValues } from './coupon-plan-scopes';
import { CouponPlanScopesForm } from './coupon-plan-scopes.form';

export class CouponCreateForm {
    private couponsService: CouponsService;
    private tradingPlanService: TradingPlanService;
    private subscriptions: Subscription[] = [];
    public formGroup: FormGroup;
    public controls: CreateCouponFormControlsInterface;
    public planScopesForm: CouponPlanScopesForm;

    constructor(couponsService: CouponsService, tradingPlanService: TradingPlanService) {
        this.couponsService = couponsService;
        this.tradingPlanService = tradingPlanService;
    }

    /**
     * The backend requires at least one plan scope, and the plan list and plan-type picker together
     * make up that collection — so the rule cannot live on a single control's validators.
     */
    private validatePlanScopes(): boolean {
        this.controls.planScopePlanTypeIds.resetErrors();

        if (this.getPlanScopes().length > 0) {
            return true;
        }

        this.controls.planScopePlanTypeIds.setError(CouponFormMessages()[CouponPlanScopeValidatorKey]);

        return false;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = CreateCouponControls();
        this.planScopesForm = new CouponPlanScopesForm(this.tradingPlanService);

        const values = Object.values(this.controls) as BaseControl<any>[];
        values.forEach((control: BaseControl<any>) => {
            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));
        });

        const discountTypeControl = this.formGroup.get(CreateCouponRequestModel.getDiscountTypeSelector());
        this.subscriptions.push(
            discountTypeControl.valueChanges.subscribe((discountType: number) => this.discountTypeChanged(discountType))
        );
        this.discountTypeChanged(discountTypeControl.value);
    }

    destroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
        this.subscriptions = [];
    }

    createRequest(): ServerResponse<CouponDetailInterface> {
        if (!this.formGroup.dirty && !this.planScopesForm.form.dirty) {
            return null;
        }

        const v = this.formGroup.value;

        const model = new CreateCouponRequestModel(
            v[CreateCouponRequestModel.getCodeSelector()],
            v[CreateCouponRequestModel.getDiscountTypeSelector()],
            // The amount input holds a formatted string ("1 000.50"), the endpoint wants a number.
            Helper.getAmountValue(v[CreateCouponRequestModel.getDiscountValueSelector()]),
            v[CreateCouponRequestModel.getCurrencyCodeSelector()],
            v[CreateCouponRequestModel.getUserEligibilityTypeSelector()],
            v[CreateCouponRequestModel.getDescriptionSelector()] || null,
            v[CreateCouponRequestModel.getPaymentMethodSelector()],
            CouponCapValue(v[CreateCouponRequestModel.getGlobalRedemptionCapSelector()]),
            CouponCapValue(v[CreateCouponRequestModel.getPerUserRedemptionCapSelector()]),
            v[CreateCouponRequestModel.getStartDateTimeUtcSelector()],
            v[CreateCouponRequestModel.getEndDateTimeUtcSelector()],
            CouponPortalIdValue(v[CreateCouponRequestModel.getPortalIdSelector()]),
            this.getPlanScopes(),
            v[CreateCouponRequestModel.getActivateImmediatelySelector()],
            v[CreateCouponRequestModel.getApplicableToTypeSelector()]
        );

        return this.couponsService.create(model);
    }

    getPlanScopes(): CreateCouponPlanScopeInterface[] {
        return CouponPlanScopesFromControlValues(
            this.planScopesForm.getPlanIds(),
            this.formGroup.get(this.controls.planScopePlanTypeIds.name)?.value
        );
    }

    discountTypeChanged(discountType: number): void {
        this.controls.discountValue.isPercentage = discountType === CouponDiscountTypeEnum.Percentage;

        const formControl = this.formGroup.get(CreateCouponRequestModel.getDiscountValueSelector());

        formControl.setValidators(CouponDiscountValueValidators(discountType));
        formControl.updateValueAndValidity();
    }

    validateForm(
        messagesFunction: (controlsLabel?: string, controlLength?: number) => { [key: string]: string }
    ): boolean {
        const values = Object.values(this.controls) as BaseControl<any>[];
        const results = values.map((control: BaseControl) => {
            const formControl = this.formGroup.controls[control.name];

            if (formControl && formControl.invalid) {
                control.errors = CouponControlErrorMessages(control, formControl.errors, messagesFunction);

                return false;
            }

            return true;
        });

        return [
            ...results,
            this.planScopesForm.validateForm(messagesFunction),
            this.validatePlanScopes(),
            CouponRedemptionCapsValid(
                this.formGroup,
                this.controls.globalRedemptionCap,
                this.controls.perUserRedemptionCap,
                messagesFunction
            )
        ].indexOf(false) < 0;
    }
}
